import React from 'react';
import { MapPin, Calendar, ExternalLink, Briefcase } from 'lucide-react';

const ExperienceCard = ({ experience }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105">
      <div className="p-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
              <img
                src={experience.companyLogo}
                alt={`${experience.company} logo`}
                className="w-12 h-12 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <Briefcase className="w-8 h-8 text-gray-400" style={{ display: 'none' }} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {experience.jobTitle}
              </h3>
              <a
                href={experience.companyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 font-semibold hover:underline flex items-center space-x-1"
              >
                <span>{experience.company}</span>
                {experience.companyUrl !== '#' && <ExternalLink className="w-4 h-4" />}
              </a>
            </div>
          </div>
          {experience.isCurrent && (
            <span className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 px-4 py-2 rounded-full text-sm font-bold">
              Current Position
            </span>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-left">Overview</h4>
            <p className="text-gray-700 dark:text-gray-300 mb-4">{experience.description}</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(experience.startDate)} - {formatDate(experience.endDate)}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                <MapPin className="w-4 h-4" />
                <span>{experience.location} • {experience.employmentType}</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-left">Technologies</h4>
            <div className="flex flex-wrap gap-2 mb-4">
              {experience.technologies.map((tech, idx) => (
                <span
                  key={idx}
                  className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 px-3 py-1 rounded-full text-sm font-medium"
                >
                  {tech}
                </span>
              ))}
            </div>

            <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-left">Key Achievements</h4>
            <ul className="space-y-1">
              {experience.achievements.slice(0, 3).map((achievement, idx) => (
                <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600 dark:text-gray-300">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>{achievement}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperienceCard;